import useSWR from 'swr';
import { fetcher } from '../../../services/fetcher';
import { moduleConfig } from './supplierQuotationsModuleConfig';
import {
  buildListQuery,
  ListQueryOptions,
  normalizePaginatedListResponse,
  PaginatedApiResponse,
} from '../../../services/paginatedList';

export interface SupplierQuotationLine {
  id?: number;
  itemId?: number | null;
  itemVcode?: string | null;
  itemDescr?: string | null;
  unitId?: number | null;
  qty?: number | null;
  unitPrice?: string | number | null;
  lineTotal?: string | number | null;
  discount?: string | number | null;
  vat?: string | number | null;
  requisitionQty?: number | null;
  enquiryQty?: number | null;
  itemNotes?: string | null;
  item?: {
    level1?: string | null;
    level2?: string | null;
    level3?: string | null;
  } | null;
  unit?: { id?: number; ucode?: string | null; descr?: string | null } | null;
}

export interface SupplierQuotationExpense {
  id?: number;
  description?: string | null;
  value?: string | number | null;
  vat?: string | number | null;
}

export interface SupplierQuotation {
  id: number;
  quoDate?: string | null;
  caseId: number;
  supplierId: number;
  supplierDate?: string | null;
  ref?: string | null;
  enqId: number;
  currencyId?: number | null;
  rate?: string | number | null;
  supplierNotes?: string | null;
  paymentTerms?: string | null;
  supplierRef?: string | null;
  requisitionId?: number | null;
  notes?: string | null;
  rateDate?: string | null;
  createdAt: string;
  updatedAt: string;
  lineItems?: SupplierQuotationLine[];
  expenses?: SupplierQuotationExpense[];
  _count?: { lineItems: number };
  supplyCase?: {
    id: number;
    ref?: string | null;
    vessel?: { id: number; vesselName?: string | null; vesselAbrv?: string | null } | null;
  } | null;
  enquiry?: {
    id: number;
    ref?: string | null;
    enqDate?: string | null;
    rfqPublicSubmittedAt?: string | null;
  } | null;
  rfqPortalLastOpenedAt?: string | null;
  rfqPortalLastSavedAt?: string | null;
  requisition?: { id: number; ref?: string | null; reqDate?: string | null } | null;
  supplier?: { id: number; name: string; code?: string | null; email?: string | null; isActive?: boolean } | null;
  currency?: { id: number; code: string; name: string; symbol?: string | null; isActive?: boolean } | null;
}

export const useSupplierQuotations = (
  searchParams?: string,
  page = 1,
  limit = 25,
  options?: ListQueryOptions,
) => {
  const q = buildListQuery(searchParams, page, limit, options);
  const { data, error, isLoading, mutate } = useSWR<
    PaginatedApiResponse<SupplierQuotation> | SupplierQuotation[]
  >(`${moduleConfig.module.api.list}${q}`, fetcher, { revalidateOnFocus: true });
  const normalized = normalizePaginatedListResponse<SupplierQuotation>(data, page, limit);
  return {
    data: normalized.data,
    clients: normalized.data,
    total: normalized.total,
    page: normalized.page,
    limit: normalized.limit,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useSupplierQuotation = (id?: number) => {
  const { data, error, isLoading, mutate } = useSWR<SupplierQuotation>(
    id ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  return { data, item: data, isLoading, isError: error, mutate };
};

